import React, { useState } from 'react';
import PageHero from '@/components/PageHero';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { DONATE } from '@/data/donate';
import { Heart, ShieldCheck, ExternalLink, Info } from 'lucide-react';

const PayPalMark = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <path
      fill="currentColor"
      d="M7.4 21.5H4.6l.4-2.5h2.8l-.4 2.5zm11.3-14.4c.6.7.9 1.7.7 3-.6 3.3-3 4.6-6.1 4.6h-.7c-.4 0-.7.3-.8.6l-.5 3.4-.2 1c0 .3-.3.5-.6.5H8l1.6-10.5c.1-.4.4-.6.8-.6h4c1.8 0 3.2.4 4 1.3.2.2.3.4.3.7zm-3.5-4.6H10c-.4 0-.7.3-.8.6L6.5 18.4h3l.7-4.6c.1-.4.4-.6.8-.6h1.4c3.5 0 6.2-1.4 7-5.2.2-1.5-.1-2.7-1-3.5-1-.9-2.7-1-4.2-1z"
    />
  </svg>
);

export default function Donate() {
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState('');

  const finalAmount = custom ? Number(custom) : amount;
  const paypalWithAmount = `${DONATE.paypalUrl}&amount=${finalAmount || 25}`;

  return (
    <div>
      <SEO
        title="Support Our Mission"
        description="Help fund the Drug Awareness USA educational mission. Voluntary contributions support free drug awareness resources and educational outreach."
        path="/donate"
      />
      <PageHero
        title={DONATE.headline}
        subtitle="Your voluntary contribution helps us reach more students with reliable drug and alcohol awareness education."
        image="https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?auto=format&fit=crop&w=1920&q=80"
        crumbs={['Support']}
      />

      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Mission statement */}
        <div className="mb-12 p-8 bg-white rounded-lg border border-slate-200 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-md bg-[#0b2545] text-amber-400 flex items-center justify-center flex-shrink-0">
              <Heart className="h-6 w-6" />
            </div>
            <div>
              <h2 className="font-serif text-2xl font-bold text-[#0b2545] mb-3">
                Why give?
              </h2>
              <p className="text-slate-700 leading-relaxed">{DONATE.body}</p>
            </div>
          </div>
        </div>

        {/* Amount picker */}
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-amber-600 font-semibold mb-3">Choose an amount</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {DONATE.amounts.map((a) => (
              <button
                key={a}
                onClick={() => {
                  setAmount(a);
                  setCustom('');
                }}
                className={`py-4 rounded-md border-2 font-serif text-xl font-bold transition-all ${
                  amount === a && !custom
                    ? 'border-amber-500 bg-amber-50 text-[#0b2545]'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-amber-300'
                }`}
              >
                ${a}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 max-w-md">
            <label htmlFor="custom" className="text-sm text-slate-600 whitespace-nowrap">
              Or custom amount:
            </label>
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
              <input
                id="custom"
                type="number"
                min="1"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="Enter amount"
                className="w-full pl-7 pr-4 py-2.5 rounded-md border border-slate-300 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
              />
            </div>
          </div>
        </div>

        {/* PayPal button */}
        <div className="mb-10">
          <a
            href={paypalWithAmount}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-center gap-3 px-6 py-5 rounded-md bg-[#ffc439] hover:bg-[#f0b429] text-[#003087] font-bold text-lg transition-all shadow-sm hover:shadow-md w-full"
          >
            <PayPalMark />
            <span>Donate ${finalAmount || 25} with PayPal</span>
            <ExternalLink className="h-4 w-4 opacity-60 group-hover:translate-x-0.5 transition-transform" />
          </a>
          <p className="text-xs text-slate-500 text-center mt-3">
            You'll be redirected to PayPal to complete your donation securely.
          </p>
        </div>

        {/* Trust notice */}
        <div className="p-6 bg-slate-50 rounded-lg border border-slate-200 flex items-start gap-4">
          <ShieldCheck className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-[#0b2545] mb-1">Secure processing</div>
            <p className="text-sm text-slate-600 leading-relaxed">
              Payments are processed securely by PayPal. We never see or store your payment card details.
              Contributions go directly to Kress &amp; Associates to support the Drug Awareness USA mission.
            </p>
          </div>
        </div>

        {/* Tax disclaimer */}
        <div className="mt-6 p-6 bg-amber-50 rounded-lg border border-amber-200 flex items-start gap-4">
          <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-slate-700 leading-relaxed">
            <strong>Please note:</strong> Drug Awareness USA is a sole proprietorship. Contributions are
            <strong> not tax-deductible</strong> charitable donations. We appreciate your voluntary support
            of our educational mission.
          </p>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 p-8 bg-[#0b2545] text-white rounded-lg text-center">
          <h3 className="font-serif text-2xl font-bold mb-2">Prefer to help another way?</h3>
          <p className="text-slate-300 mb-5">
            Invite the program to your school, share our resources, or reach out to us directly.
          </p>
          <Button asChild className="bg-amber-500 hover:bg-amber-400 text-[#0b2545] font-semibold">
            <a href="/contact">Contact Us</a>
          </Button>
        </div>
      </div>
    </div>
  );
}
